import React from 'react';
import Button from '../../common/Button/Button';
import localStyles from './AdvancedDropdownExpandButton.module.pcss';
import { useClassNameMapper } from 'react-bootstrap';
import { FocusElementType } from '../../../types/enums';
import { ButtonVariant } from '../../common/Button/enums';
import Icon from '../../common/Icon/Icon';
import { IconSize, IconColor } from '../../common/Icon/enums';
import IconTypes from '../../../public/static/graphics/processed/enums';
import useTranslation from '../../useTranslation';
import { SharedComponent } from '../../../enums';

interface Props {
  /** Callback fired when button is clicked */
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

  /** Callback fired when keydown event occurs while button is focused */
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;

  /**
   * Index of the item associated with the button. This shouldn't be necessary,
   * but is left intact for the pre-existing keyboard navigation code,
   * which may be cleaned up in a later commit. TODO
   */
  idx: number;

  /** Optional testId. A default is provided. */
  testId?: string;

  /** Optional node title to include in aria-label for better accessibility */
  nodeTitle?: string;
}

/**
 * Renders the expand container node component in the dropdown menu item
 * @author Jonathan Bridges, Rosalyn Rowe
 */
const AdvancedDropdownExpandButton: React.FC<Props> = ({
  onClick,
  onKeyDown = () => null,
  idx,
  testId,
  nodeTitle
}) => {
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    SharedComponent.ADVANCED_DROPDOWN_EXPAND_BUTTON
  );

  if (textLoading) {
    return null;
  }

  const ariaLabel = nodeTitle
    ? formatMessage('expandButtonAriaWithTitle', { nodeTitle })
    : formatMessage('expandButtonAria');

  return (
    <Button
      variant={ButtonVariant.UNSTYLED}
      className={cx('lia-g-icon-btn lia-expand-btn')}
      onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onClick(event)}
      onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => onKeyDown(event)}
      data-value={`${FocusElementType.NODE_EXPAND}-${idx}`}
      data-node-expand-btn={idx}
      data-testid={testId ?? `AdvancedDropdownExpandButton-${idx}`}
      aria-label={ariaLabel}
    >
      <Icon size={IconSize.PX_16} icon={IconTypes.ChevronRightIcon} color={IconColor.GRAY_900} />
    </Button>
  );
};

export default AdvancedDropdownExpandButton;
